import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { mcpClient } from "../../lib/mcp-client";
import { queryKeys } from "../../lib/query-keys";
import type { RowDetailResult } from "../../lib/types";

/**
 * Sprint 12 — the single read backing the RowDetail panel/page. One
 * `get_row_detail` call returns the record's fields, workflow transitions,
 * owned files/folders, resolved forward references, and grouped back-references.
 * Polls at 30s so content added via MCP/Claude shows up without a manual reload.
 */
export function useRowDetail(entityTypeSlug: string | undefined, entityId: string | undefined) {
  return useQuery<RowDetailResult>({
    queryKey:
      entityTypeSlug && entityId
        ? queryKeys.rowDetail(entityTypeSlug, entityId)
        : ["row-detail", "", ""],
    queryFn: () => {
      if (!entityTypeSlug || !entityId) throw new Error("row unresolved");
      return mcpClient.getRowDetail(entityTypeSlug, entityId);
    },
    enabled: !!entityTypeSlug && !!entityId,
    staleTime: 10_000,
    refetchInterval: 30_000,
  });
}

/**
 * Create a folder under an entity row. Passing the row's entity_id as parent_id
 * lazily promotes the row server-side (transparent). Invalidates the row detail
 * so the new folder appears, plus the tree/children caches the FilesPanel reads.
 */
export function useCreateRowFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { name: string; entityTypeSlug: string; entityId: string }) =>
      mcpClient.createFolder({ name: vars.name, parent_id: vars.entityId }),
    onSuccess: (_node, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.rowDetail(vars.entityTypeSlug, vars.entityId) });
      qc.invalidateQueries({ queryKey: queryKeys.libraryTree() });
      qc.invalidateQueries({ queryKey: ["library-children"] });
      qc.invalidateQueries({ queryKey: queryKeys.workspace() });
    },
  });
}

/**
 * Upload a file under an entity row. The row is lazily promoted and the file
 * filed beneath it server-side. Invalidates the row detail + the entity_ref
 * file list so both the panel's Files section and the cockpit refresh.
 */
export function useUploadRowFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      file: File;
      entityTypeSlug: string;
      entityId: string;
    }) => {
      const { content, encoding } = await readBrowserFile(vars.file);
      return mcpClient.uploadFile({
        filename: vars.file.name,
        content,
        encoding,
        mime_type: vars.file.type || undefined,
        entity_ref: vars.entityId,
      });
    },
    onSuccess: (_file, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.rowDetail(vars.entityTypeSlug, vars.entityId) });
      qc.invalidateQueries({ queryKey: queryKeys.files(vars.entityId) });
    },
  });
}

/** Read a browser File into the shape upload_file expects (utf-8 or base64). */
function readBrowserFile(file: File): Promise<{ content: string; encoding: "utf-8" | "base64" }> {
  const isText = file.type.startsWith("text/") || /\.(md|markdown|txt|csv|json|ya?ml)$/i.test(file.name);
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error("read_failed"));
    if (isText) {
      reader.onload = () => resolve({ content: String(reader.result ?? ""), encoding: "utf-8" });
      reader.readAsText(file);
    } else {
      reader.onload = () => resolve({ content: String(reader.result ?? ""), encoding: "base64" });
      reader.readAsDataURL(file);
    }
  });
}
